import * as React from 'react';
import { FormControl } from 'react-bootstrap';
import type { FieldValues, UseFormSetValue } from 'react-hook-form';
import type { Path } from 'react-hook-form';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import DateInput from 'af-components/Controls/DateInput';
import type { Props as PlainDatePickerProps } from 'af-components/Controls/DateInput';

import styles from './styles.module.scss';
import type { CellError } from '../../error.types';

type FieldValue = string | number | undefined;

/**
 * RHFFieldProps are props coming from field in react-hook-form
 */
interface RHFFieldProps<TFieldValues extends FieldValues = FieldValues,
> {
	name: string;
	value: FieldValue;
	onChange: React.ChangeEventHandler;
	onBlur: () => void;
	onFocus: () => void;
	setValue: UseFormSetValue<TFieldValues>;
}

type Props = RHFFieldProps & PlainDatePickerProps & {
	elementKey: string;
	placeholder?: string;
	className?: string;
	error?: CellError;
};

function asPath<T>(p: string): Path<T> {
	return p as Path<T>;
}

function DatePickerCell<TFieldValues extends FieldValues = FieldValues>(props): React.ReactElement<Props> {
	const { elementKey, value, onChange, autoOpen, error, setValue, name, dateFormat } = props;

	// QA id is automatically generated to help with automated tests
	// We want to avoid accidentally forgetting to place an id on an element, making it harder for QA to write automated tests, so we keep it generated
	// Styling should never come from id, anyway
	const id = React.useMemo(() => (
		`accqa__datepicker__${elementKey}`
	), [elementKey]);

	const handleChange = React.useCallback((date: Date) => {
		if (onChange) {
			onChange(event);
		}
		setValue(asPath<TFieldValues>(name), TimeUtils.formatDate(date, dateFormat), { shouldDirty: true, shouldTouch: true });
	}, [dateFormat, name, onChange, setValue]);

	const datePickerClassName = React.useMemo(() => {
		if (!error) {
			return undefined;
		}
		return styles['error-msg'];
	}, [error]);

	const renderCell = React.useMemo(() => (
		<>
			<DateInput
				{...props}
				autoFocus={autoOpen}
				calendarIconAdditionalClassName={styles['calendar-icon']}
				datePickerClassName={datePickerClassName}
				error={error?.message}
				fixed={true}
				id={id}
				isStandalone={true}
				key={elementKey}
				onValueChange={handleChange}
				selectedValue={value ?? undefined}
				showTodayButton={false}
			/>
			<FormControl.Feedback />
		</>
	), [autoOpen, datePickerClassName, elementKey, handleChange, id, props, value, error]);

	return renderCell;
}

export default React.memo(DatePickerCell) as typeof DatePickerCell;
